import { useEffect, useState } from "react";

const SDK_SCRIPT_ID = "vitrina-tv-sdk-script";
const DEFAULT_SDK_SRC = "/static/vendor/js/websdk.js";

let sdkPromise = null;

function loadVitrinaSdkScript(src = DEFAULT_SDK_SRC) {
  if (typeof window === "undefined") {
    return Promise.resolve(false);
  }

  if (window.VitrinaTVSDK) {
    return Promise.resolve(true);
  }

  if (sdkPromise) {
    return sdkPromise;
  }

  sdkPromise = new Promise((resolve, reject) => {
    const existingScript = document.getElementById(SDK_SCRIPT_ID);

    const onLoad = () => {
      if (window.VitrinaTVSDK) {
        const loadedScript = document.getElementById(SDK_SCRIPT_ID);
        if (loadedScript) {
          loadedScript.dataset.loaded = "true";
        }
        resolve(true);
        return;
      }

      sdkPromise = null;
      reject(new Error("Vitrina TV SDK loaded but unavailable"));
    };

    const onError = () => {
      sdkPromise = null;
      reject(new Error("Failed to load Vitrina TV SDK"));
    };

    if (existingScript) {
      if (
        window.VitrinaTVSDK ||
        existingScript.dataset.loaded === "true" ||
        existingScript.readyState === "complete" ||
        existingScript.complete
      ) {
        onLoad();
        return;
      }

      existingScript.addEventListener("load", onLoad, { once: true });
      existingScript.addEventListener("error", onError, { once: true });
      return;
    }

    const script = document.createElement("script");
    script.id = SDK_SCRIPT_ID;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.dataset.loaded = "false";
    script.addEventListener("load", onLoad, { once: true });
    script.addEventListener("error", onError, { once: true });
    document.head.appendChild(script);
  });

  return sdkPromise;
}

export default function useLoadVitrinaSdk({
  enabled = true,
  src = DEFAULT_SDK_SRC,
} = {}) {
  const [isLoaded, setIsLoaded] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return Boolean(window.VitrinaTVSDK);
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") {
      return;
    }

    let isCancelled = false;
    const load = async () => {
      try {
        await loadVitrinaSdkScript(src);

        if (isCancelled) {
          return;
        }

        setIsLoaded(true);
        setError(null);
      } catch (loadError) {
        if (isCancelled) {
          return;
        }

        setIsLoaded(false);
        setError(loadError);
      }
    };
    load();

    return () => {
      isCancelled = true;
    };
  }, [enabled, src]);

  return { isLoaded, error };
}
